/**
 * Demo Mode Context — Shared Read-Only Demo Organization
 *
 * REQUIREMENTS SUMMARY:
 * - Provides isDemoMode flag (derived from currentOrganization.is_demo).
 * - enterDemoMode(realOrgId): grants a dedicated demo-viewer pass on the shared demo
 *   org (public.demo_org_viewers, #1354 — no organization_staff row), switches context.
 * - exitDemoMode(): removes the demo-viewer pass from the shared demo org, switches back.
 * - enterDemoModeAgain(realOrgId): same as enterDemoMode (no re-seeding needed).
 * - skipDemoMode(realOrgId): marks real org demo_prompt_shown=true without entering.
 * - realOrganizationId: persisted to localStorage so exit can restore the real org.
 * - Loading states (isEnteringDemo, isExitingDemo) for UI feedback.
 * - All errors via useErrorReportContext — never alert().
 * - Near-instant enter/exit (1-2 DB ops instead of 26+).
 */

"use client";

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useOrganization } from '@/context/OrganizationContext';
import { useAuth } from '@/context/AuthContext';
import { useErrorReportContext } from '@/context/ErrorReportContext';
import { supabase } from '@/utils/supabase/client';
import { demoLoadingState } from '@/utils/demo/demoLoadingState';
import { SHARED_DEMO_ORG_ID, DEMO_REAL_ORG_STORAGE_KEY } from '@/utils/demo/demoConfig';
import { createLogger } from '@/utils/logger/devLogger';
import { clearAllDemoTeamBuilderStates } from '@/services/teamBuilder/demoStorage';
import { rememberOrganization } from '@/utils/organizations/rememberedOrganization';

const logger = createLogger('DemoContext');

const REAL_ORG_STORAGE_KEY = DEMO_REAL_ORG_STORAGE_KEY;

// ── Helpers ──────────────────────────────────────────────────────────────────

async function getBearerToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

async function callDemoApi(
  path: string,
  body: Record<string, string>,
  token: string,
): Promise<{ ok: boolean; data?: any; error?: string }> {
  const res = await fetch(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    return { ok: false, error: json?.error ?? `Request failed (${res.status})` };
  }
  // successResponse wraps payload in { success, data }; unwrap so callers get data directly
  return { ok: true, data: json?.data ?? json };
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface DemoContextType {
  isDemoMode: boolean;
  isEnteringDemo: boolean;
  isExitingDemo: boolean;
  /** The real org ID the user had before entering demo (null when not in demo). */
  realOrganizationId: string | null;
  enterDemoMode: (realOrgId: string) => Promise<void>;
  exitDemoMode: () => Promise<void>;
  enterDemoModeAgain: (realOrgId: string) => Promise<void>;
  /**
   * Skip demo — just mark prompt as shown and stay in real org.
   * Resolves `true` only when the flag actually flipped. The welcome modal has no close
   * button, so its dismissal depends entirely on this write landing; a caller that
   * navigates away on a silent failure strands an un-dismissable modal (#2672).
   */
  skipDemoMode: (realOrgId: string) => Promise<boolean>;
}

const DemoContext = createContext<DemoContextType>({
  isDemoMode: false,
  isEnteringDemo: false,
  isExitingDemo: false,
  realOrganizationId: null,
  enterDemoMode: async () => {},
  exitDemoMode: async () => {},
  enterDemoModeAgain: async () => {},
  skipDemoMode: async () => false,
});

// ── Provider ──────────────────────────────────────────────────────────────────

export function DemoProvider({ children }: { children: React.ReactNode }) {
  const { currentOrganization, organizations, refreshOrganizations, setCurrentOrganization } =
    useOrganization();
  const { user } = useAuth();
  const { showError } = useErrorReportContext();
  const router = useRouter();

  const [isEnteringDemo, _setIsEnteringDemo] = useState(false);
  const [isExitingDemo, _setIsExitingDemo] = useState(false);

  // Sync module-level store so the overlay survives provider remounts
  const setIsEnteringDemo = useCallback((val: boolean) => {
    _setIsEnteringDemo(val);
    demoLoadingState.setEntering(val);
  }, []);
  const setIsExitingDemo = useCallback((val: boolean) => {
    _setIsExitingDemo(val);
    demoLoadingState.setExiting(val);
  }, []);

  const isDemoMode = currentOrganization?.is_demo === true;

  // Read real org ID from localStorage (persisted so page refreshes survive)
  const realOrganizationId =
    typeof window !== 'undefined' ? localStorage.getItem(REAL_ORG_STORAGE_KEY) : null;

  // ── Internal: switch to the shared demo org ────────────────────────────

  const switchToDemoOrg = useCallback(
    async (demoOrgId: string) => {
      // Pre-set localStorage so fetchOrganizations (inside refreshOrganizations)
      // picks up the demo org instead of the old org — prevents a race where
      // LocationContext/SeasonContext fire with the stale org before we override.
      rememberOrganization(demoOrgId, user?.id);
      await refreshOrganizations();
      // After refresh, verify the demo org is set
      const { data: orgRows } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', demoOrgId)
        .single();
      if (orgRows) {
        setCurrentOrganization(orgRows as any);
      } else {
        // Fallback: find by is_demo in the refreshed list
        const demoOrg = organizations.find((o) => o.id === demoOrgId);
        if (demoOrg) setCurrentOrganization(demoOrg);
      }
    },
    [refreshOrganizations, setCurrentOrganization, organizations, user?.id],
  );

  // ── Internal: switch back to real org ───────────────────────────────────

  const switchToRealOrg = useCallback(
    async (realOrgId: string) => {
      // Pre-set localStorage so fetchOrganizations restores the real org immediately
      rememberOrganization(realOrgId, user?.id);
      await refreshOrganizations();
      const { data: orgRows } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', realOrgId)
        .single();
      if (orgRows) {
        setCurrentOrganization(orgRows as any);
      }
      localStorage.removeItem(REAL_ORG_STORAGE_KEY);
    },
    [refreshOrganizations, setCurrentOrganization, user?.id],
  );

  // ── enterDemoMode ────────────────────────────────────────────────────────

  const enterDemoMode = useCallback(
    async (realOrgId: string) => {
      if (!user) return;
      setIsEnteringDemo(true);
      try {
        const token = await getBearerToken();
        if (!token) throw new Error('Not authenticated');

        localStorage.setItem(REAL_ORG_STORAGE_KEY, realOrgId);

        const result = await callDemoApi('/api/demo/enter', { organizationId: realOrgId }, token);
        if (!result.ok) throw new Error(result.error ?? 'Demo setup failed');

        await switchToDemoOrg(result.data.demoOrgId);
        logger.info('Entered demo mode', { demoOrgId: result.data.demoOrgId });
        router.push('/dashboard');
      } catch (err) {
        localStorage.removeItem(REAL_ORG_STORAGE_KEY);
        logger.error('Failed to enter demo mode', err);
        showError({
          title: 'Demo Setup Failed',
          message:
            'We were not able to load the demo gym. Please try again or contact support if the issue continues.',
          type: 'system',
        });
        setIsEnteringDemo(false);
      }
    },
    [user, switchToDemoOrg, showError, router, setIsEnteringDemo],
  );

  // ── exitDemoMode ─────────────────────────────────────────────────────────

  const exitDemoMode = useCallback(async () => {
    if (!currentOrganization?.id) return;
    const demoOrgId = currentOrganization.id;
    const savedRealOrgId = localStorage.getItem(REAL_ORG_STORAGE_KEY);

    setIsExitingDemo(true);
    try {
      const token = await getBearerToken();
      if (!token) throw new Error('Not authenticated');

      const result = await callDemoApi('/api/demo/exit', { demoOrgId }, token);
      if (!result.ok) throw new Error(result.error ?? 'Exit failed');

      // Clean up any demo Team Builder state saved in localStorage
      clearAllDemoTeamBuilderStates();

      if (savedRealOrgId) {
        await switchToRealOrg(savedRealOrgId);
      } else {
        await refreshOrganizations();
      }
      logger.info('Exited demo mode', { demoOrgId });

      // If the real org is inactive (expired trial / suspended), send to account-inactive
      // instead of the dashboard to prevent subscription guard bypass.
      const { data: realOrg } = savedRealOrgId
        ? await supabase.from('organizations').select('is_active, name, id').eq('id', savedRealOrgId).single()
        : { data: null };

      if (realOrg && realOrg.is_active === false) {
        const inactiveUrl = `/auth/account-inactive?orgName=${encodeURIComponent(realOrg.name || 'your gym')}&orgId=${realOrg.id}`;
        router.push(inactiveUrl);
      } else {
        router.push('/dashboard');
      }
    } catch (err) {
      logger.error('Failed to exit demo mode', err);
      showError({
        title: 'Could Not Exit Demo',
        message: 'Something went wrong while leaving the demo. Please try again.',
        type: 'system',
      });
      setIsExitingDemo(false);
    }
  }, [currentOrganization, switchToRealOrg, refreshOrganizations, showError, setIsExitingDemo, router]);

  // ── enterDemoModeAgain ───────────────────────────────────────────────────
  // Same as enterDemoMode — no re-seeding needed with shared org approach.

  const enterDemoModeAgain = useCallback(
    async (realOrgId: string) => {
      await enterDemoMode(realOrgId);
    },
    [enterDemoMode],
  );

  // ── skipDemoMode ─────────────────────────────────────────────────────────

  const skipDemoMode = useCallback(async (realOrgId: string): Promise<boolean> => {
    try {
      // Two ways this write fails without throwing, and the caller navigates away on a
      // false success in both: Supabase reports errors via `error` rather than throwing,
      // AND an update blocked by RLS matches zero rows and comes back error-free. The
      // `.select('id')` is what distinguishes "wrote it" from "matched nothing".
      const { data, error } = await supabase
        .from('organizations')
        .update({ demo_prompt_shown: true } as any)
        .eq('id', realOrgId)
        .select('id');
      if (error) {
        logger.error('Failed to skip demo', error);
        return false;
      }
      if (!data || data.length === 0) {
        logger.error('Skip demo matched no rows — the flag did not flip', { realOrgId });
        return false;
      }
      // Refresh so currentOrganization.demo_prompt_shown updates
      await refreshOrganizations();
      logger.info('Demo prompt dismissed', { realOrgId });
      return true;
    } catch (err) {
      logger.error('Failed to skip demo', err);
      return false;
    }
  }, [refreshOrganizations]);

  // ── Auto-clear overlay flags once org transition is confirmed ─────────────
  // enterDemoMode / exitDemoMode no longer clear in `finally` — they leave the
  // overlay up so the user doesn't see the old page flash.  These effects
  // detect when the org has actually switched and dismiss after a render delay.

  useEffect(() => {
    if (!demoLoadingState.getSnapshot().isEntering) return;
    if (!isDemoMode) return; // still waiting for demo org

    // Demo org is now active — keep overlay a bit longer for page render
    const timer = setTimeout(() => setIsEnteringDemo(false), 800);
    return () => clearTimeout(timer);
  }, [isDemoMode, setIsEnteringDemo]);

  useEffect(() => {
    if (!demoLoadingState.getSnapshot().isExiting) return;
    if (isDemoMode) return; // still on demo org
    if (!currentOrganization) return; // real org not set yet

    // Real org is now active — dismiss overlay after brief delay
    const timer = setTimeout(() => setIsExitingDemo(false), 500);
    return () => clearTimeout(timer);
  }, [isDemoMode, currentOrganization, setIsExitingDemo]);

  // Safety timeout: prevent overlay from being stuck indefinitely
  useEffect(() => {
    const snapshot = demoLoadingState.getSnapshot();
    if (!snapshot.isEntering && !snapshot.isExiting) return;

    const safety = setTimeout(() => {
      const current = demoLoadingState.getSnapshot();
      if (current.isEntering) {
        logger.warn('Safety timeout: clearing stuck entering overlay after 15s');
        setIsEnteringDemo(false);
      }
      if (current.isExiting) {
        logger.warn('Safety timeout: clearing stuck exiting overlay after 15s');
        setIsExitingDemo(false);
      }
    }, 15000); // Reduced from 30s — enter/exit are near-instant now

    return () => clearTimeout(safety);
  }, [isEnteringDemo, isExitingDemo, setIsEnteringDemo, setIsExitingDemo]);

  const value: DemoContextType = {
    isDemoMode,
    isEnteringDemo,
    isExitingDemo,
    realOrganizationId,
    enterDemoMode,
    exitDemoMode,
    enterDemoModeAgain,
    skipDemoMode,
  };

  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useDemo() {
  const context = useContext(DemoContext);
  if (!context) {
    throw new Error('useDemo must be used within a DemoProvider');
  }
  return context;
}
